/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable, inject, signal } from '@angular/core';
import { FrameworkDetails, FrameworkTagDetails } from '@app/app/shared/models/admin/admin.model';
import { AutocompleteSearchOption } from '@app/app/shared/styleguide/components/autocomplete-search/sg-autocomplete-search.component';
import { Observable, catchError, of, shareReplay } from 'rxjs';
import { FrameworkV3Service } from './framework-v3.service';

/**
 * The frameworks evidence blocks take their tags from, loaded once per page however many blocks ask. Authors pick from
 * the organisation's list (`list`); a learner's block loads just its own framework (`get`), which any signed-in user may
 * read.
 */
@Injectable({ providedIn: 'root' })
export class FrameworkCatalog {
  private readonly service = inject(FrameworkV3Service);

  private readonly frameworks = signal<FrameworkDetails[]>([]);
  private readonly listLoaded = signal(false);
  private listStarted = false;
  private readonly byUid = new Map<string, Observable<FrameworkDetails | null>>();

  /** The organisation's frameworks, empty until loaded or when they can't be. */
  readonly list = this.frameworks.asReadonly();
  readonly ready = this.listLoaded.asReadonly();

  /** Starts loading the list. Safe to call from every block. */
  loadList(): void {
    if (this.listStarted) {
      return;
    }
    this.listStarted = true;
    this.service
      .getFrameworks()
      .pipe(
        catchError(() => {
          // Let a later block try again.
          this.listStarted = false;
          return of([] as FrameworkDetails[]);
        }),
      )
      .subscribe((frameworks) => {
        this.frameworks.set(frameworks);
        this.listLoaded.set(true);
      });
  }

  /** One framework with its tags; null when it can't be read. */
  get(frameworkUid: string): Observable<FrameworkDetails | null> {
    let framework$ = this.byUid.get(frameworkUid);
    if (!framework$) {
      framework$ = this.service.getFramework(frameworkUid).pipe(
        catchError(() => {
          // Let a later block try again.
          this.byUid.delete(frameworkUid);
          return of(null);
        }),
        shareReplay(1),
      );
      this.byUid.set(frameworkUid, framework$);
    }
    return framework$;
  }
}

/**
 * A framework's tags at every depth, for a tag picker: each with its clean title as `label` and its ancestors
 * (`Parent › Child`) as `sublabel`, so a match keeps its context, and the search finds tags by their parents too.
 */
export function frameworkTagOptions(framework: FrameworkDetails | null | undefined): AutocompleteSearchOption<string>[] {
  return flattenTags(framework?.tags ?? [], []);
}

function flattenTags(tags: FrameworkTagDetails[], ancestry: string[]): AutocompleteSearchOption<string>[] {
  return tags.flatMap((tag) => [
    { value: tag.tagId, label: tag.title, sublabel: ancestry.length ? ancestry.join(' › ') : undefined },
    ...flattenTags(tag.children ?? [], [...ancestry, tag.title]),
  ]);
}
